import Page from '@/components/Layout/Page';
import React from 'react';
import { Form, Formik } from 'formik';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import FormikGotoErrorOnSubmitEffectFn from '@/components/Inputs/FormikGotoErrorOnSubmitEffect';
import useLocalSetting from '@/hooks/useLocalSetting';
import { useCan } from '@/hooks/useRbac';
import { useOptionalFilterState, type SelectedFilter } from '@/hooks/useFilter';
import { useLocation } from 'react-router';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  BilderitModuleId,
  useGetMyReportSubscriptionsQuery,
  useUnsubscribeFromBilderitReportMutation,
} from '@/generated/codegen';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { NewBilderitReportsSubscriptionButton } from './NewBilderitReportsSubscriptionDialog';
import { Loader2Icon } from 'lucide-react';
import { BILDERIT_REPORT_TYPES } from './AppSchema';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { NotificationSettings } from './NotificationSettings';

const options = [
  { label: 'interno' as const, value: 'name' },
  { label: 'marca' as const, value: 'brand' },
  { label: 'modelo' as const, value: 'model' },
  { label: 'año' as const, value: 'year' },
  { label: 'tipo' as const, value: 'display_type' },
];

const getOptionValue = (label: string) =>
  options.find((option) => option.label === label)?.value;
const getOptionLabel = (value: string) =>
  options.find((option) => option.value === value)
    ?.label as (typeof options)[number]['label'];

const labelsToValues = (opts: (typeof options)[number]['label'][]) =>
  opts.map(getOptionValue).filter((field): field is string => !!field);

const ejemplo = {
  interno: 'AB123',
  marca: 'Caterpillar',
  modelo: 'X125',
  año: '1999',
  tipo: 'Topadora',
};

const SettingsPage: React.FC = () => {
  const filter = useOptionalFilterState();
  const { hash } = useLocation();
  const can = useCan();
  const [machineDisplayName, setMachineDisplayName] = useLocalSetting(
    'machine-display-name',
    ['name', 'brand', 'model']
  );
  const [serviceWarningThreshold, setServiceWarningThreshold] = useLocalSetting(
    'service-warning-threshold',
    0.2
  );

  React.useEffect(() => {
    const element = document.getElementById(hash.replace('#', ''));
    if (!hash || !element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [hash]);

  return (
    <Page>
      <div className="flex flex-col max-w-2xl mx-auto gap-6">
        <Formik
          initialValues={{
            display_name:
              machineDisplayName?.map((value) => getOptionLabel(value)) ?? [],
          }}
          enableReinitialize
          onSubmit={(values, { setSubmitting }) => {
            setMachineDisplayName(labelsToValues(values['display_name']));
            setSubmitting(false);
          }}
        >
          {({ values, status, isSubmitting, setFieldValue }) => (
            <Form>
              <Card id="machines">
                <CardHeader>
                  <CardTitle>Maquinas</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* <FormGroup row={true} className="items-center"> */}
                  <div className="items-center">
                    <Label htmlFor="machine-display-name">
                      Nombre de maquinas
                    </Label>
                    {/* <ButtonGroup
                      id="machine-display-name"
                      style={{
                        flexWrap: 'wrap',
                        height: 'fit-content',
                      }}
                    > */}
                    <div
                      id="machine-display-name"
                      style={{
                        flexWrap: 'wrap',
                        height: 'fit-content',
                      }}
                    >
                      {[...(values['display_name'] ?? []), undefined].map(
                        (field, k) => (
                          <DropdownMenu key={k}>
                            <DropdownMenuTrigger>
                              {field ?? <FaPlus />}
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuRadioGroup
                                value={values.display_name[k]}
                                onValueChange={(value) =>
                                  setFieldValue('display_name', [
                                    ...values.display_name.slice(0, k),
                                    value,
                                    ...values.display_name.slice(k + 1),
                                  ])
                                }
                              >
                                {options.map(({ label }, key) => (
                                  <DropdownMenuRadioItem
                                    key={key}
                                    value={label}
                                  >
                                    {label}
                                  </DropdownMenuRadioItem>
                                ))}
                              </DropdownMenuRadioGroup>
                              {!!field && values.display_name.length > 1 && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setFieldValue('display_name', [
                                        ...values.display_name.slice(0, k),
                                        ...values.display_name.slice(k + 1),
                                      ])
                                    }
                                  >
                                    <FaTimes /> Limpiar
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )
                      )}
                    </div>
                  </div>
                  <hr />
                  Ejemplo:{' '}
                  {values.display_name
                    .map((selected) => ejemplo[selected])
                    .join(' ')}
                  <span style={{ color: 'red' }}>{status?.error?.message}</span>
                </CardContent>
                <CardFooter>
                  <Button
                    variant="success"
                    className="float-right"
                    disabled={
                      isSubmitting ||
                      (labelsToValues(values['display_name']).length ===
                        machineDisplayName.length &&
                        labelsToValues(values['display_name']).every(
                          (label, index) => label === machineDisplayName[index]
                        ))
                    }
                    type="submit"
                  >
                    {isSubmitting && <LoadingSpinner />}
                    {!isSubmitting && 'Actualizar'}
                  </Button>
                </CardFooter>
              </Card>
              <FormikGotoErrorOnSubmitEffectFn />
            </Form>
          )}
        </Formik>
        {can('services:config') && (
          <Formik
            initialValues={{
              alertThreshold: serviceWarningThreshold * 100,
            }}
            enableReinitialize
            onSubmit={(values, { setSubmitting }) => {
              setServiceWarningThreshold(values.alertThreshold / 100);
              setSubmitting(false);
            }}
          >
            {({ values, status, isSubmitting, setFieldValue }) => (
              <Form>
                <Card id="services">
                  <CardHeader>
                    <CardTitle>Services</CardTitle>
                  </CardHeader>
                  <CardContent>
                    En la página de seguimiento de services, los equipos pasan a
                    estado de alerta (color amarillo) al quedarles{' '}
                    {(values.alertThreshold * 1).toFixed(0)}% de horas o
                    kilómetros para su próximo service
                    <Separator className="my-2" />
                    <Label>Próximos Services (hs restantes)</Label>
                    <div className="relative h-4 w-full overflow-hidden rounded-full bg-primary/20 text-xs text-end">
                      <div
                        className="absolute bg-destructive h-full flex-1 transition-all"
                        style={{ width: '100%' }}
                      >
                        Atrasado!
                      </div>
                      <div
                        className="absolute bg-warning h-full flex-1 transition-all"
                        style={{ width: `50%` }}
                      >
                        Alerta!
                      </div>
                      <div
                        className="absolute bg-success h-full w-full flex-1 transition-all"
                        style={{
                          width: `${
                            ((100 - values.alertThreshold) / 100) * 50
                          }%`,
                        }}
                      >
                        {100 - values.alertThreshold}%
                      </div>
                    </div>
                    <div className="inline-flex justify-between w-full text-xs">
                      <div>100%</div>
                      <div className="flex-1 text-right">0%</div>
                      <div className="w-1/2" />
                    </div>
                    <Separator className="my-2" />
                    <Label>Próximos Services (% atrasado)</Label>
                    <div className="relative h-4 w-full overflow-hidden rounded-full bg-primary/20 text-xs text-end">
                      <div
                        className="absolute bg-destructive h-full flex-1 transition-all"
                        style={{ width: '100%' }}
                      >
                        Atrasado!
                      </div>
                      <div
                        className="absolute bg-warning h-full flex-1 transition-all"
                        style={{ width: `${(50 / 100) * 50 + 50}%` }}
                      >
                        50%
                      </div>
                      <div
                        className="absolute bg-success h-full w-full flex-1 transition-all"
                        style={{
                          width: `${
                            ((100 - values.alertThreshold) / 100) * 50 + 50
                          }%`,
                        }}
                      >
                        {100 - values.alertThreshold}%
                      </div>
                    </div>
                    <div className="inline-flex justify-between w-full text-xs">
                      <div className="w-1/2" />
                      <div className="flex-1 text-left">0%</div>
                      <div>100%</div>
                    </div>
                    <Separator className="my-2" />
                    <Label>Nivel de Alerta</Label>
                    <Slider
                      name="alertThreshold"
                      value={[values.alertThreshold]}
                      max={100}
                      onValueChange={(value) =>
                        setFieldValue('alertThreshold', value)
                      }
                    />
                    <span style={{ color: 'red' }}>
                      {status?.error?.message}
                    </span>
                  </CardContent>
                  <CardFooter>
                    <Button
                      variant="success"
                      className="float-right"
                      disabled={
                        isSubmitting ||
                        values.alertThreshold * 1 ===
                          serviceWarningThreshold * 100
                      }
                      type="submit"
                    >
                      {isSubmitting && <LoadingSpinner />}
                      {!isSubmitting && 'Actualizar'}
                    </Button>
                  </CardFooter>
                </Card>
                <FormikGotoErrorOnSubmitEffectFn />
              </Form>
            )}
          </Formik>
        )}
        <NotificationSettings />
        {can('reports:settings') && filter.kind === 'selected' && (
          <ReportsSettings filter={filter} />
        )}
      </div>
    </Page>
  );
};

export default SettingsPage;

const ReportsSettings: React.FC<{ filter: SelectedFilter }> = ({ filter }) => {
  const companyId = filter.query.company_id;
  const { isFeatureEnabled } = useFeatureFlags();

  const { data: reportsSubscriptionsData, loading } =
    useGetMyReportSubscriptionsQuery({
      variables: { context: { company_id: companyId } },
      notifyOnNetworkStatusChange: true,
    });
  const reports = React.useMemo(
    () => reportsSubscriptionsData?.getMyBilderitReportSubscriptions ?? [],
    [reportsSubscriptionsData]
  );
  const [unsubscribe, { loading: unsubscribing }] =
    useUnsubscribeFromBilderitReportMutation({
      refetchQueries: ['GetMyReportSubscriptions'],
    });

  return isFeatureEnabled(BilderitModuleId.WeeklyReports) ? (
    <Card id="reports">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Reportes
          <NewBilderitReportsSubscriptionButton />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul>
          {loading ? (
            <li>
              <Skeleton className="h-8 w-full" />
            </li>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Compañía</TableHead>
                  <TableHead>Proyecto</TableHead>
                  <TableHead>Reporte</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>{report.company.name}</TableCell>
                    <TableCell>{report.project?.name ?? '-'}</TableCell>
                    <TableCell>
                      {BILDERIT_REPORT_TYPES.find(
                        ({ value }) => value === report.report_type
                      )?.label ?? 'Desconocido'}
                    </TableCell>
                    <TableCell>{report.email}</TableCell>
                    <TableCell>
                      <Button
                        variant="destructive"
                        size="md-icon"
                        onClick={() =>
                          unsubscribe({ variables: { id: report.id } })
                        }
                        disabled={unsubscribing}
                      >
                        {unsubscribing ? (
                          <Loader2Icon className="animate-spin" />
                        ) : (
                          <FaTimes />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ul>
      </CardContent>
    </Card>
  ) : null;
};
